import type { LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

import { Switch } from "@/components/ui/switch"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

// A capability and everything it needs, in one box — the Compass treatment
// from the home-feed design: hairline border, muted icon, and the dependent
// config revealed under a hairline rule when the switch is on.
//
// It replaces a card that held only the toggle, with config in a flat run of
// sections below it — a tab with three capabilities gave no clue which field
// belonged to which switch. The cards also stack full width rather than
// sitting in a two-column grid: a capability owns a row, and its config opens
// underneath it.
export function CapabilityCard({
  icon: Icon,
  on,
  title,
  description,
  disabled = false,
  disabledNote,
  onToggle,
  children,
}: {
  icon: LucideIcon
  on: boolean
  title: string
  description?: string
  disabled?: boolean
  disabledNote?: string
  onToggle?: (on: boolean) => void
  children?: ReactNode
}) {
  return (
    <div className={cn("rounded-lg border", disabled && "opacity-60")}>
      <div className="p-4">
        {/* Icon and switch share the top row so the title and description
            below them run the full width of the card rather than wrapping
            early against the switch. */}
        <div className="flex items-start justify-between gap-4">
          <Icon className="text-muted-foreground size-5" />
          {disabled && disabledNote ? (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Wrapper receives hover — a disabled switch doesn't emit
                    pointer events on its own. */}
                <span className="inline-flex shrink-0" tabIndex={0}>
                  <Switch
                    checked={on}
                    disabled
                    className="pointer-events-none"
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>{disabledNote}</TooltipContent>
            </Tooltip>
          ) : (
            <Switch
              checked={on}
              disabled={disabled}
              onCheckedChange={onToggle}
              className="shrink-0"
            />
          )}
        </div>
        <p className="mt-3 text-sm/6 font-semibold">{title}</p>
        {description && (
          <p className="text-muted-foreground mt-0.5 text-sm/6">
            {description}
          </p>
        )}
      </div>

      {on && children && (
        <div className="space-y-6 border-t p-4">{children}</div>
      )}
    </div>
  )
}
